/**
 * AZEX Review Points — review-and-clearance UI.
 *
 * Exposes three composable pieces:
 *   • <ReviewControls target={..} /> — badge + Add Review button + drawer
 *   • <ReviewCountsProvider engagementId={..} /> — bulk-fetches counts once
 *     per engagement so a 100+ row Vouching sheet issues ONE API call
 *     instead of one per entry, then feeds child ReviewControls via context.
 *   • <ReviewCountBadge target={..} /> — read-only badge, no drawer.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { useNavigate } from "react-router-dom";
import DateInput from "@/components/DateInput";
import { useAuth } from "@/contexts/AuthContext";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import AttachmentPanel from "@/components/AttachmentPanel";
import {
  MessageSquarePlus, CheckCircle2, RotateCcw, Trash2, PlayCircle, Send,
  ExternalLink,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { fmtDate, fmtDateTimeFirm } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)

// Roles that may CREATE review points (spec §permissions.review_point.create
// + legacy Reviewer/Audit Manager for existing users).
const RAISER_ROLES = new Set([
  "Supervisor", "Assistant Manager", "Manager", "Senior Manager",
  "Partner", "EQC", "Reviewer", "Audit Manager",
]);
const PREPARER_ROLES = new Set(["Preparer", "Auditor"]);

const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const STATUS_COLOR = {
  "Pending": "bg-red-100 text-red-800 border-red-300",
  "In Progress": "bg-orange-100 text-orange-800 border-orange-300",
  "Resolved": "bg-blue-100 text-blue-800 border-blue-300",
  "Cleared": "bg-emerald-100 text-emerald-800 border-emerald-300",
};

// -------------------- ReviewCountsProvider --------------------
const CountsCtx = createContext(null);

export function ReviewCountsProvider({ engagementId, targetType, children }) {
  const [countsMap, setCountsMap] = useState({});   // { "type:id": {open, total, cleared} }
  const [tick, setTick] = useState(0);

  const reload = useCallback(async () => {
    if (!engagementId) return;
    try {
      const params = targetType ? `?target_type=${encodeURIComponent(targetType)}` : "";
      const r = await api.get(
        `/engagements/${engagementId}/review-points/counts${params}`,
        { _silent: true });
      setCountsMap(r.data?.counts || {});
    } catch { /* silent — badges just won't show */ }
  }, [engagementId, targetType]);

  useEffect(() => { reload(); }, [reload, tick]);

  const bump = useCallback(() => setTick((t) => t + 1), []);
  const value = useMemo(() => ({ countsMap, reload, bump }),
                          [countsMap, reload, bump]);
  return <CountsCtx.Provider value={value}>{children}</CountsCtx.Provider>;
}

export function useReviewCounts() {
  return useContext(CountsCtx) || { countsMap: {}, reload: () => {}, bump: () => {} };
}

// -------------------- Small badge --------------------
export function ReviewCountBadge({ targetType, targetId, onClick, size = "sm" }) {
  const { lang, dict, t } = useI18n();
  const { countsMap } = useReviewCounts();
  const key = `${targetType}:${targetId}`;
  const c = countsMap[key];
  if (!c || c.total === 0) return null;
  const cls = size === "xs"
    ? "min-w-[16px] h-4 text-[9px] px-1"
    : "min-w-[20px] h-5 text-[10px] px-1.5";
  const tone = c.open > 0
    ? "bg-red-600 text-white border-red-700"
    : "bg-emerald-600 text-white border-emerald-700";
  return (
    <button type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-full border font-bold tabular-nums ${cls} ${tone} hover:brightness-110`}
      data-testid={`rp-badge-${targetType}-${targetId}`}
      title={t("reviewPoints.badgeSummary")
        .replace("{plural}", fmtPlural(c.total, lang, dict.plurals.reviewPoints))
        .replace("{open}", String(c.open))
        .replace("{cleared}", String(c.cleared))}>
      {c.total}
    </button>
  );
}

// -------------------- Main <ReviewControls> --------------------
// Renders the Add Review button (if raiser) + a count badge and opens
// the ReviewPointsDrawer when either is clicked.
export function ReviewControls({
  engagementId, targetType, targetId, vouchingSheetId, targetLabel,
  compact = false,
}) {
  const { user } = useAuth() || {};   // Batch 28 · item 8: the bar is on screens (and tests) outside the auth provider too
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const isRaiser = RAISER_ROLES.has(user?.role);

  const btnClass = compact
    ? "h-6 px-1.5 text-[10px]"
    : "h-7 px-2 text-xs";

  return (
    <span className="inline-flex items-center gap-1"
          data-testid={`rp-controls-${targetType}-${targetId}`}>
      <ReviewCountBadge targetType={targetType} targetId={targetId}
                          onClick={() => setOpen(true)}
                          size={compact ? "xs" : "sm"} />
      {isRaiser && (
        <Button type="button" size="sm" variant="outline"
                onClick={() => setOpen(true)}
                className={`gap-1 ${btnClass}`}
                data-testid={`rp-add-${targetType}-${targetId}`}
                title={t("reviewPoints.raiseTip")}>
          <MessageSquarePlus className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />
          {!compact && t("reviewPoints.raiseAdd")}
        </Button>
      )}
      {open && (
        <ReviewPointsDrawer
          engagementId={engagementId}
          targetType={targetType} targetId={targetId}
          vouchingSheetId={vouchingSheetId} targetLabel={targetLabel}
          onClose={() => setOpen(false)} />
      )}
    </span>
  );
}


// -------------------- Drawer (list + create + comment) --------------------
export function ReviewPointsDrawer({
  engagementId, targetType, targetId, vouchingSheetId, targetLabel, onClose,
}) {
  const { user } = useAuth();
  const { t } = useI18n();
  const isRaiser = RAISER_ROLES.has(user?.role);
  const isPreparer = PREPARER_ROLES.has(user?.role);
  const { bump } = useReviewCounts();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newText, setNewText] = useState("");
  const [newPriority, setNewPriority] = useState("Medium");
  const [newDue, setNewDue] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/engagements/${engagementId}/review-points`,
        { params: { target_type: targetType, target_id: targetId } });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(toErrMsg(e, t("reviewPoints.failedLoad")));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId, targetType, targetId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newText.trim()) { toast.error(t("reviewPoints.enterText")); return; }
    setCreating(true);
    try {
      await api.post(`/engagements/${engagementId}/review-points`, {
        target_type: targetType,
        target_id: targetId,
        vouching_sheet_id: vouchingSheetId || null,
        review_point_text: newText.trim(),
        priority: newPriority,
        due_date: newDue || null,
      });
      toast.success(t("reviewPoints.raised"));
      setNewText(""); setNewPriority("Medium"); setNewDue("");
      await load(); bump();
    } catch (e) {
      toast.error(toErrMsg(e, t("reviewPoints.failedRaise")));
    } finally { setCreating(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="!max-w-2xl max-h-[90vh] overflow-y-auto"
                      data-testid="rp-drawer">
        <DialogHeader>
          <DialogTitle className="text-base">{t("reviewPoints.panelTitle")}</DialogTitle>
          {targetLabel && (
            <DialogDescription className="text-xs">{targetLabel}</DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">{t("reviewPoints.loading")}</div>
        ) : (
          <div className="space-y-3">
            {rows.length === 0 && (
              <div className="rounded border border-dashed p-3 text-center text-xs text-muted-foreground"
                    data-testid="rp-empty">
                {t("reviewPoints.empty")}
              </div>
            )}
            {rows.map((rp) => (
              <ReviewPointCard key={rp.id} rp={rp}
                onChanged={async () => { await load(); bump(); }}
                canRaise={isRaiser} isPreparer={isPreparer}
                selfId={user?.user_id || user?.id}
                engagementId={engagementId} />
            ))}

            {isRaiser && (
              <div className="rounded border p-3 space-y-2 bg-slate-50 dark:bg-slate-900/40"
                    data-testid="rp-create-form">
                <div className="text-xs font-medium">{t("reviewPoints.newTitle")}</div>
                <Textarea rows={3} value={newText} onChange={(e) => setNewText(e.target.value)}
                          placeholder={t("reviewPoints.newPlaceholder")}
                          data-testid="rp-new-text" />
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-32">
                    <Label className="text-[10px]">{t("reviewPoints.priority")}</Label>
                    <Select value={newPriority} onValueChange={setNewPriority}>
                      <SelectTrigger className="h-8 text-xs" data-testid="rp-new-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-40">
                    <Label className="text-[10px]">{t("reviewPoints.dueOptional")}</Label>
                    <DateInput value={newDue}
                            onChange={(v) => setNewDue(v)}
                            className="w-full h-8 rounded border px-2 text-xs dark:bg-slate-950"
                            data-testid="rp-new-due" />
                  </div>
                  <Button size="sm" onClick={create} disabled={creating}
                          className="ms-auto" data-testid="rp-new-submit">
                    <Send className="me-1 h-3 w-3" />
                    {creating ? t("reviewPoints.raising") : t("reviewPoints.raise")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// -------------------- ReviewPointCard --------------------
function ReviewPointCard({ rp, onChanged, canRaise, isPreparer, selfId, engagementId, locked }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [comments, setComments] = useState(null);

  const isOwner = rp.raised_by_user_id === selfId;
  const status = rp.status;
  const canClear = isOwner && status === "Resolved";
  const canReopen = isOwner && status === "Cleared";
  const canDelete = isOwner;
  // Mar 2026 · anyone with view access can advance status Pending →
  // In Progress → Resolved. Only Clear/Reopen stay raiser-only, so a
  // reviewer who raises a "note to self" can resolve it themselves.
  const canProgress = status === "Pending" && !rp.is_deleted;
  const canResolve = (status === "Pending" || status === "In Progress")
                       && !rp.is_deleted;

  const act = async (path, verb) => {
    setBusy(true);
    try {
      await api.post(`/review-points/${rp.id}/${path}`);
      toast.success(verb);
      onChanged();
    } catch (e) {
      toast.error(toErrMsg(e, `${verb} failed`));
    } finally { setBusy(false); }
  };

  const submitComment = async () => {
    if (!commentText.trim()) { toast.error(t("reviewPoints.enterComment")); return; }
    setBusy(true);
    try {
      await api.post(`/review-points/${rp.id}/comments`,
                        { comment_text: commentText.trim() });
      setCommentText(""); setCommentOpen(false);
      // Reload the comment list (if the timeline is showing).
      if (comments !== null) {
        const r = await api.get(`/review-points/${rp.id}`);
        setComments(r.data.comments || []);
      }
      toast.success(t("reviewPoints.commentAdded"));
      onChanged();
    } catch (e) {
      toast.error(toErrMsg(e, t("reviewPoints.commentFailed")));
    } finally { setBusy(false); }
  };

  const loadHistory = async () => {
    if (history !== null) { setShowHistory((v) => !v); return; }
    try {
      const [h, d] = await Promise.all([
        api.get(`/review-points/${rp.id}/history`),
        api.get(`/review-points/${rp.id}`),
      ]);
      setHistory(h.data.events || []);
      setComments(d.data.comments || []);
      setShowHistory(true);
    } catch (e) {
      toast.error(toErrMsg(e, t("reviewPoints.loadHistoryFailed")));
    }
  };

  const del = async () => {
    if (!await askConfirm(t("reviewPoints.deleteConfirm"))) return;
    setBusy(true);
    try {
      await api.delete(`/review-points/${rp.id}`);
      toast.success(t("reviewPoints.deleted")); onChanged();
    } catch (e) {
      toast.error(toErrMsg(e, t("reviewPoints.deleteFailed")));
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded border p-3 ${rp.is_deleted ? "opacity-60 border-dashed" : ""}`}
          data-testid={`rp-card-${rp.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_COLOR[status] || ""}`}
                    data-testid={`rp-status-${rp.id}`}>{status}</span>
            <span className="text-[10px] text-muted-foreground">{t("reviewPoints.priorityPrefix").replace("{p}", rp.priority)}</span>
            {rp.is_deleted && <span className="text-[10px] font-medium text-red-700">{t("reviewPoints.deletedTag")}</span>}
          </div>
          <div className="mt-1 text-sm whitespace-pre-wrap"
                data-testid={`rp-text-${rp.id}`}>{rp.review_point_text}</div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            {t("reviewPoints.raisedByLine")
              .replace("{name}", rp.raised_by_name || "")
              .replace("{role}", rp.raised_by_role || "")
              .replace("{date}", fmtDate(rp.created_at))}
            {rp.due_date && t("reviewPoints.dueSuffix").replace("{date}", fmtDate(rp.due_date))}
          </div>
        </div>
        {rp.target_href && (
          <Button type="button" size="sm" variant="outline"
                    className="h-7 shrink-0 gap-1 px-2 text-[10px]"
                    onClick={() => navigate(rp.target_href)}
                    title={rp.target_label
                              ? t("reviewPoints.openSourceLabel").replace("{label}", rp.target_label)
                              : t("reviewPoints.openSource")}
                    data-testid={`rp-open-source-${rp.id}`}>
            <ExternalLink className="h-3 w-3" />
            {t("reviewPoints.openSource")}
          </Button>
        )}
      </div>

      {!rp.is_deleted && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {canProgress && (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                    disabled={busy} onClick={() => act("in-progress", t("reviewPoints.markedInProgress"))}
                    data-testid={`rp-inprogress-${rp.id}`}>
              <PlayCircle className="me-1 h-3 w-3" /> {t("reviewPoints.inProgressBtn")}
            </Button>
          )}
          {canResolve && (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                    disabled={busy} onClick={() => act("resolve", t("reviewPoints.markedResolved"))}
                    data-testid={`rp-resolve-${rp.id}`}>
              <CheckCircle2 className="me-1 h-3 w-3" /> {t("reviewPoints.resolveBtn")}
            </Button>
          )}
          {canClear && (
            <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700"
                    disabled={busy} onClick={() => act("clear", t("reviewPoints.cleared"))}
                    data-testid={`rp-clear-${rp.id}`}>
              <CheckCircle2 className="me-1 h-3 w-3" /> {t("reviewPoints.clearBtn")}
            </Button>
          )}
          {canReopen && (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                    disabled={busy} onClick={() => act("reopen", t("reviewPoints.reopened"))}
                    data-testid={`rp-reopen-${rp.id}`}>
              <RotateCcw className="me-1 h-3 w-3" /> {t("reviewPoints.reopenBtn")}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                  onClick={() => setCommentOpen((v) => !v)}
                  data-testid={`rp-comment-toggle-${rp.id}`}>
            <MessageSquarePlus className="me-1 h-3 w-3" /> {t("reviewPoints.commentBtn")}
          </Button>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                  onClick={loadHistory}
                  data-testid={`rp-history-${rp.id}`}>
            {showHistory ? t("reviewPoints.hideBtn") : t("reviewPoints.historyBtn")}
          </Button>
          {canDelete && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-red-700 hover:bg-red-50"
                    disabled={busy} onClick={del}
                    data-testid={`rp-delete-${rp.id}`}>
              <Trash2 className="me-1 h-3 w-3" /> {t("reviewPoints.deleteBtn")}
            </Button>
          )}
        </div>
      )}

      {commentOpen && (
        <div className="mt-2 space-y-1">
          <Textarea rows={2} value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={t("reviewPoints.commentPh")}
                    data-testid={`rp-comment-text-${rp.id}`} />
          <div className="flex justify-end">
            <Button size="sm" className="h-6 text-[10px]"
                    onClick={submitComment} disabled={busy}
                    data-testid={`rp-comment-submit-${rp.id}`}>
              {t("reviewPoints.postComment")}
            </Button>
          </div>
        </div>
      )}

      {showHistory && (comments || history) && (
        <div className="mt-2 rounded border bg-slate-50 dark:bg-slate-900/30 p-2 space-y-2"
              data-testid={`rp-history-panel-${rp.id}`}>
          {(comments || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("reviewPoints.commentsLabel")}</div>
              {asArray(comments).map((c) => (
                <div key={c.id} className="mt-1 text-xs">
                  <b>{c.author_name}</b> <span className="text-[10px] text-muted-foreground">{t("reviewPoints.commentByLine")
                    .replace("{role}", c.author_role || "")
                    .replace("{when}", fmtDateTimeFirm(c.created_at))}</span>
                  <div className="whitespace-pre-wrap">{c.comment_text}</div>
                </div>
              ))}
            </div>
          )}
          {(history || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("reviewPoints.timelineLabel")}</div>
              {asArray(history).map((h) => (
                <div key={h.id} className="mt-0.5 text-[10px] text-muted-foreground">
                  <span className="tabular-nums">{fmtDateTimeFirm(h.created_at)}</span> ·
                  <b className="ms-1">{h.user_name}</b> ({h.user_role}) — {h.action.replace(/_/g, " ")}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* v55 — attachments linked to THIS review point (not just its engagement) */}
      {engagementId && rp.id && !rp.is_deleted && (
        <div className="mt-2">
          <AttachmentPanel
            engagementId={engagementId}
            contextType="review_point"
            contextId={rp.id}
            locked={!!locked}
            label={t("reviewPoints.attachmentsLabel")}
            compact
          />
        </div>
      )}
    </div>
  );
}
